import type { ActionContext, Commit, Module } from 'vuex';
import type { UsedOptions } from '../interfaces/block';
import { getCrmRequests } from '../api/api';
import { commitEntity } from './helpers';
import type {
    Configuration,
    ConfigurationNamePayload,
    PlaceBlockPayload,
    Stage,
    StageFieldFlagsPayload,
    StageWritePayload,
    UpdatePlacedBlockPayload,
} from '../interfaces/configuration';

/**
 * Nav: **Configuration CRM** — the pipeline list and, per pipeline, the Stages & blocks and Pipeline &
 * Champs tabs. See docs/CRM_SECTION_CONTRACT.md §6.
 *
 * Addresses as `crm/configurationCrm/<action>`, state at `state.crm.configurationCrm`. **This is the
 * single source of the pipeline list** — the selector in `store/pipeline.ts` reads `configurations` from
 * here rather than fetching its own copy.
 *
 * **A team has one or more configurations (AQ-11065), and every endpoint returns the whole document it
 * wrote**, so there is one write path — `UPSERT_CONFIGURATION` — and one rule that covers all three
 * tracks: *commit what the server returned, never hand-edit state.* That is what keeps D (stages), E
 * (placed blocks) and F (identity + field settings) from clobbering each other; a mutation per sub-path
 * is what would not.
 *
 * `selectedStageIds` is keyed **per configuration id**, because several cards can be expanded at once
 * (§6) — two expanded cards must not share one selected stage.
 */
export interface ConfigurationCrmState {
    configurations: Configuration[];
    defaultConfigurationId: string | null;
    /** Whether the list has been fetched at least once — lets `pipeline.ts#initPipelines` avoid a refetch. */
    loaded: boolean;
    selectedStageIds: Record<string, string | null>;
    loading: boolean;
    /** An i18n key, never raw exception text — the view renders it as-is. */
    error: string | null;
    /** By configuration id. */
    usedOptions: Record<string, UsedOptions>;
}

const defaultState = (): ConfigurationCrmState => ({
    configurations: [],
    defaultConfigurationId: null,
    loaded: false,
    selectedStageIds: {},
    loading: false,
    error: null,
    usedOptions: {},
});

async function commitWrite(
    commit: Commit,
    request: () => Promise<Configuration>,
): Promise<Configuration | null> {
    const configuration = await commitEntity(commit, request, 'crm.configurationCrm.errors.saveFailed');
    if (!configuration) {
        return null;
    }

    commit('UPSERT_CONFIGURATION', configuration);
    return configuration;
}

type Context = ActionContext<ConfigurationCrmState, unknown>;

export const configurationCrmModule: Module<ConfigurationCrmState, unknown> = {
    namespaced: true,
    state: defaultState,
    mutations: {
        SET_USED_OPTIONS(
            state: ConfigurationCrmState,
            { configurationId, usedOptions }: { configurationId: string; usedOptions: UsedOptions },
        ): void {
            state.usedOptions = { ...state.usedOptions, [configurationId]: usedOptions };
        },
        SET_DEFAULT_CONFIGURATION(state: ConfigurationCrmState, configurationId: string | null): void {
            state.defaultConfigurationId = configurationId;
        },
        SET_CONFIGURATIONS(state: ConfigurationCrmState, configurations: Configuration[]): void {
            state.configurations = configurations;
            state.loaded = true;
            const validIds = new Set(configurations.map((configuration) => configuration.id));
            for (const configurationId of Object.keys(state.selectedStageIds)) {
                if (!validIds.has(configurationId)) {
                    delete state.selectedStageIds[configurationId];
                }
            }
        },
        UPSERT_CONFIGURATION(state: ConfigurationCrmState, configuration: Configuration): void {
            const index = state.configurations.findIndex((candidate) => candidate.id === configuration.id);
            state.configurations = index === -1
                ? [...state.configurations, configuration]
                : state.configurations.map((candidate) =>
                    (candidate.id === configuration.id ? configuration : candidate));

            const selected = state.selectedStageIds[configuration.id];
            const stillExists = configuration.stages.some((stage) => stage.id === selected);
            if (selected && !stillExists) {
                state.selectedStageIds = { ...state.selectedStageIds, [configuration.id]: null };
            }
        },
        REMOVE_CONFIGURATION(state: ConfigurationCrmState, configurationId: string): void {
            state.configurations = state.configurations.filter(
                (configuration) => configuration.id !== configurationId,
            );
            delete state.selectedStageIds[configurationId];
        },
        SELECT_STAGE(
            state: ConfigurationCrmState,
            { configurationId, stageId }: { configurationId: string; stageId: string | null },
        ): void {
            state.selectedStageIds = { ...state.selectedStageIds, [configurationId]: stageId };
        },
        SET_LOADING(state: ConfigurationCrmState, value: boolean): void {
            state.loading = value;
        },
        SET_ERROR(state: ConfigurationCrmState, message: string | null): void {
            state.error = message;
        },
    },
    actions: {
        async fetchConfigurations({ commit, state }: Context, { force = false }: { force?: boolean } = {}): Promise<void> {
            if (state.loaded && !force) {
                return;
            }
            commit('SET_LOADING', true);
            commit('SET_ERROR', null);
            try {
                const { configurations, defaultConfiguration } = await getCrmRequests().configurations.list();
                commit('SET_CONFIGURATIONS', configurations);
                commit('SET_DEFAULT_CONFIGURATION', defaultConfiguration ?? null);
            } catch {
                commit('SET_ERROR', 'crm.configurationCrm.errors.loadFailed');
            } finally {
                commit('SET_LOADING', false);
            }
        },
        async fetchUsedOptions({ commit }: Context, configurationId: string): Promise<void> {
            try {
                const { usedOptions } = await getCrmRequests().configurations.usedOptions(configurationId);
                commit('SET_USED_OPTIONS', { configurationId, usedOptions });
            } catch {
                // The server still refuses removing a used value; only the greyed-out hint is lost.
            }
        },
        async createConfiguration({ commit }: Context, name: string): Promise<Configuration | null> {
            return commitWrite(commit, () => getCrmRequests().configurations.create({ name }));
        },
        async renameConfiguration(
            { commit }: Context,
            { configurationId, payload }: { configurationId: string; payload: ConfigurationNamePayload },
        ): Promise<boolean> {
            return !!(await commitWrite(commit, () =>
                getCrmRequests().configurations.rename(configurationId, payload)));
        },
        async deleteConfiguration({ commit }: Context, configurationId: string): Promise<boolean> {
            commit('SET_ERROR', null);
            try {
                await getCrmRequests().configurations.remove(configurationId);
                commit('REMOVE_CONFIGURATION', configurationId);
                return true;
            } catch {
                commit('SET_ERROR', 'crm.configurationCrm.errors.saveFailed');
                return false;
            }
        },
        /** Track D. */
        async createStage({ commit }: Context, configurationId: string): Promise<boolean> {
            return !!(await commitWrite(commit, () =>
                getCrmRequests().configurations.createStage(configurationId)));
        },
        /** Track D. */
        async deleteStage(
            { commit }: Context,
            { configurationId, stageId }: { configurationId: string; stageId: string },
        ): Promise<boolean> {
            return !!(await commitWrite(commit, () =>
                getCrmRequests().configurations.deleteStage(configurationId, stageId)));
        },
        async placeBlock(
            { commit }: Context,
            { configurationId, payload }: { configurationId: string; payload: PlaceBlockPayload },
        ): Promise<boolean> {
            return !!(await commitWrite(commit, () =>
                getCrmRequests().configurations.placeBlock(configurationId, payload)));
        },
        async updatePlacedBlock(
            { commit }: Context,
            { configurationId, blockId, payload }:
                { configurationId: string; blockId: string; payload: UpdatePlacedBlockPayload },
        ): Promise<boolean> {
            return !!(await commitWrite(commit, () =>
                getCrmRequests().configurations.updatePlacedBlock(configurationId, blockId, payload)));
        },
        async removePlacedBlock(
            { commit }: Context,
            { configurationId, blockId }: { configurationId: string; blockId: string },
        ): Promise<boolean> {
            return !!(await commitWrite(commit, () =>
                getCrmRequests().configurations.removePlacedBlock(configurationId, blockId)));
        },
        /** Track F — stage identity. */
        async updateStage(
            { commit }: Context,
            { configurationId, stageId, payload }:
                { configurationId: string; stageId: string; payload: StageWritePayload },
        ): Promise<boolean> {
            return !!(await commitWrite(commit, () =>
                getCrmRequests().configurations.updateStage(configurationId, stageId, payload)));
        },
        /** Track F — what the fields are at one stage (visible/required, kept values). */
        async setStageFieldFlags(
            { commit, dispatch }: Context,
            { configurationId, stageId, payload }:
                { configurationId: string; stageId: string; payload: StageFieldFlagsPayload },
        ): Promise<boolean> {
            const saved = !!(await commitWrite(commit, () =>
                getCrmRequests().configurations.setStageFieldFlags(configurationId, stageId, payload)));
            if (!saved) {
                dispatch('fetchUsedOptions', configurationId);
            }
            return saved;
        },
    },
    getters: {
        configurations: (state: ConfigurationCrmState): Configuration[] => state.configurations,
        loaded: (state: ConfigurationCrmState): boolean => state.loaded,
        usedOptions: (state: ConfigurationCrmState) => (configurationId: string): UsedOptions =>
            state.usedOptions[configurationId] ?? {},
        configurationById: (state: ConfigurationCrmState) => (configurationId: string): Configuration | null =>
            state.configurations.find((configuration) => configuration.id === configurationId) ?? null,
        selectedStage: (state: ConfigurationCrmState) => (configurationId: string): Stage | null => {
            const configuration = state.configurations.find((candidate) => candidate.id === configurationId);
            const stageId = state.selectedStageIds[configurationId];
            return configuration?.stages.find((stage) => stage.id === stageId) ?? null;
        },
        loading: (state: ConfigurationCrmState): boolean => state.loading,
        error: (state: ConfigurationCrmState): string | null => state.error,
    },
};
